import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MapContainer, TileLayer, CircleMarker, useMap } from 'react-leaflet';
import { MapPin, Calendar, Users, Globe, ChevronDown, X, Eye } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Fit bounds only once on first data load ── */
const FitBoundsOnce = ({ visitors }) => {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current || visitors.length === 0) return;
    const lats = visitors.map(v => v.lat).filter(Boolean);
    const lngs = visitors.map(v => v.lng).filter(Boolean);
    if (lats.length > 0 && lngs.length > 0) {
      map.fitBounds(
        [[Math.min(...lats) - 2, Math.min(...lngs) - 5], [Math.max(...lats) + 2, Math.max(...lngs) + 5]],
        { padding: [30, 30], maxZoom: 6 }
      );
      fitted.current = true;
    }
  }, [visitors, map]);
  return null;
};

/* ── Cluster nearby visitors ── */
const clusterVisitors = (visitors, precision = 1) => {
  const clusters = {};
  visitors.forEach(v => {
    if (!v.lat || !v.lng) return;
    const key = `${v.lat.toFixed(precision)}_${v.lng.toFixed(precision)}`;
    if (!clusters[key]) {
      clusters[key] = { lat: v.lat, lng: v.lng, city: v.city, country: v.country, region: v.region, count: 0, visitors: [] };
    }
    clusters[key].count++;
    if (clusters[key].visitors.length < 10) clusters[key].visitors.push(v);
  });
  return Object.values(clusters);
};

const PERIODS = [
  { label: 'Today', days: 0 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'All time', days: 365 },
];

const VisitorMapWidget = ({ authHeaders }) => {
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodIdx, setPeriodIdx] = useState(1);
  const [showPeriodMenu, setShowPeriodMenu] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const headersRef = useRef(authHeaders);
  headersRef.current = authHeaders;

  const periodDays = PERIODS[periodIdx].days;

  const fetchVisitors = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      let start = '';
      if (periodDays > 0) {
        const d = new Date(now);
        d.setDate(d.getDate() - periodDays);
        start = d.toISOString();
      } else if (periodDays === 0) {
        const d = new Date(now);
        d.setHours(0, 0, 0, 0);
        start = d.toISOString();
      }
      const params = new URLSearchParams();
      if (start) params.set('start', start);
      params.set('end', now.toISOString());

      const res = await fetch(`${API}/api/portal/admin/visitors?${params}`, { headers: headersRef.current });
      if (res.ok) setVisitors(await res.json());
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [periodDays]);

  useEffect(() => { fetchVisitors(); }, [fetchVisitors]);

  const clusters = useMemo(() => clusterVisitors(visitors), [visitors]);
  const geoVisitors = useMemo(() => visitors.filter(v => v.lat && v.lng), [visitors]);

  const countryStats = useMemo(() => {
    const map = {};
    geoVisitors.forEach(v => { const c = v.country; if (c) map[c] = (map[c] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [geoVisitors]);

  const uniqueCities = useMemo(() => {
    const set = new Set();
    geoVisitors.forEach(v => { if (v.city) set.add(v.city); });
    return set.size;
  }, [geoVisitors]);

  return (
    <div data-testid="visitor-map-widget">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 relative z-[1100]">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFF0E8' }}>
            <MapPin size={16} style={{ color: '#E8520A' }} />
          </div>
          <div>
            <h3 className="text-sm sm:text-base font-black text-gray-900">Visitor Map</h3>
            <p className="text-[10px] text-gray-400">{geoVisitors.length} visitors with location data</p>
          </div>
        </div>

        <div className="relative" data-testid="map-period-selector">
          <button
            onClick={() => setShowPeriodMenu(!showPeriodMenu)}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-700 hover:border-gray-300 transition-all"
          >
            <Calendar size={12} style={{ color: '#E8520A' }} />
            {PERIODS[periodIdx].label}
            <ChevronDown size={12} className={`text-gray-400 transition-transform ${showPeriodMenu ? 'rotate-180' : ''}`} />
          </button>
          {showPeriodMenu && (
            <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-200 w-44 overflow-hidden z-[1100]">
              {PERIODS.map((p, i) => (
                <button
                  key={p.label}
                  onClick={() => { setPeriodIdx(i); setShowPeriodMenu(false); }}
                  className={`w-full text-left px-3 py-2 text-xs font-semibold transition-colors ${
                    periodIdx === i ? 'bg-orange-50 text-orange-700' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid={`map-period-${p.days}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Map */}
      <div className="rounded-xl overflow-hidden border border-gray-200 relative" style={{ height: 400 }} data-testid="visitor-map-container">
        {loading && (
          <div className="absolute inset-0 z-[1000] bg-white/80 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-orange-500 rounded-full animate-spin" />
          </div>
        )}
        <MapContainer
          center={[45, -75]}
          zoom={3}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          zoomControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          <FitBoundsOnce visitors={geoVisitors} />
          {clusters.map((cluster, i) => (
            <CircleMarker
              key={`${cluster.lat}-${cluster.lng}-${i}`}
              center={[cluster.lat, cluster.lng]}
              radius={Math.min(20, Math.max(6, cluster.count * 2 + 4))}
              fillColor="#E8520A"
              fillOpacity={0.7}
              stroke={true}
              color="#fff"
              weight={2}
              eventHandlers={{
                click: () => setSelectedCluster(cluster),
              }}
            />
          ))}
        </MapContainer>
      </div>

      {/* Stats below map */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
        <div className="bg-white border border-gray-100 rounded-xl p-3 text-center">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Total Visits</p>
          <p className="text-lg font-black text-gray-900">{visitors.length}</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-3 text-center">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Countries</p>
          <p className="text-lg font-black text-gray-900">{countryStats.length}</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-3 text-center">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Cities</p>
          <p className="text-lg font-black text-gray-900">{uniqueCities}</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-3 text-center">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Geo-located</p>
          <p className="text-lg font-black text-gray-900">{geoVisitors.length > 0 ? Math.round((geoVisitors.length / Math.max(1, visitors.length)) * 100) : 0}%</p>
        </div>
      </div>

      {/* Top Countries */}
      {countryStats.length > 0 && (
        <div className="mt-4 bg-white border border-gray-100 rounded-xl p-4">
          <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-2">
            <Globe size={12} style={{ color: '#E8520A' }} /> Top Countries
          </h4>
          <div className="space-y-2">
            {countryStats.map(([country, count]) => (
              <div key={country} className="flex items-center gap-3">
                <span className="text-sm text-gray-700 flex-1">{country}</span>
                <div className="flex-1 max-w-[120px] bg-gray-100 rounded-full h-1.5">
                  <div
                    className="h-1.5 rounded-full"
                    style={{ width: `${Math.round((count / geoVisitors.length) * 100)}%`, backgroundColor: '#E8520A' }}
                  />
                </div>
                <span className="text-xs font-bold text-gray-600 w-8 text-right">{count}</span>
                <span className="text-[10px] text-gray-400 w-10 text-right">{Math.round((count / geoVisitors.length) * 100)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && visitors.length === 0 && (
        <div className="text-center py-8 mt-4 bg-white border border-gray-100 rounded-xl">
          <MapPin size={32} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm font-bold text-gray-500">No visitor data for this period</p>
          <p className="text-xs text-gray-400 mt-1">Visitors will appear as they browse the site</p>
        </div>
      )}

      {/* Info Card — rendered via Portal to escape overflow:hidden parents */}
      {selectedCluster && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setSelectedCluster(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="cluster-detail-card">
            <div className="px-5 py-4 flex items-center justify-between" style={{ backgroundColor: '#1a1a1a' }}>
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-orange-400" />
                <span className="text-sm font-bold text-white">{selectedCluster.city || 'Unknown'}{selectedCluster.country ? `, ${selectedCluster.country}` : ''}</span>
              </div>
              <button onClick={() => setSelectedCluster(null)} className="text-gray-400 hover:text-white transition-colors">
                <X size={16} />
              </button>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg" style={{ backgroundColor: '#FFF0E8' }}>
                <Users size={14} style={{ color: '#E8520A' }} />
                <span className="text-sm font-bold" style={{ color: '#E8520A' }}>{selectedCluster.count} visit{selectedCluster.count > 1 ? 's' : ''}</span>
                {selectedCluster.region && <span className="text-xs text-gray-500 ml-auto">{selectedCluster.region}</span>}
              </div>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {selectedCluster.visitors.map((v, i) => (
                  <div key={v.id || v.ip || `visitor-${i}`} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                    <Eye size={11} className="text-gray-300 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-800 truncate">{v.page || '/'}</p>
                      <p className="text-[10px] text-gray-400">{v.isp || ''}</p>
                    </div>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">
                      {new Date(v.timestamp).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
                {selectedCluster.count > 10 && (
                  <p className="text-[10px] text-gray-400 text-center pt-2">+{selectedCluster.count - 10} more visits</p>
                )}
              </div>
              <p className="text-[10px] text-gray-400 mt-3 text-center">
                {selectedCluster.lat.toFixed(4)}, {selectedCluster.lng.toFixed(4)}
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default VisitorMapWidget;
